/**
 * Resolver + fuzzy-match helpers for adding system actions to workflow steps.
 *
 * Kept in its own module so tests can exercise the resolve/rank logic without
 * pulling in FormService / WorkflowScreenService / bpmn-js (which require a
 * live browser environment and blow up in plain jsdom tests).
 */

import { DashboardState, type IActionDefinitionDto } from "@kuika/kuika-cl-model";
import { buildActionFilterContextFromSd, isActionDefinitionVisible } from "./action-definition-filter";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";

export type ResolveResult = { ok: true; def: IActionDefinitionDto } | { ok: false; error: string };

interface DefLike {
  name?: string;
  Name?: string;
  isDeprecated?: boolean;
  isDeleted?: boolean;
}

function nameOf(a: IActionDefinitionDto): string {
  const d = a as DefLike;
  return (d.name || d.Name || "").toLowerCase();
}

/**
 * Resolve an action by name and validate it's usable in workflow context.
 * Requires an EXACT (case-insensitive) name match — no substring fallback,
 * which used to silently pick a different action than the LLM meant.
 * If the match isn't valid in workflow context (e.g. UI-only action), or no
 * match at all, returns an error with the closest workflow-visible
 * alternatives so the LLM can retry.
 */
export function resolveWorkflowAction(sdInstance: ScreenDesignInstance, actionName: string | undefined): ResolveResult {
  const query = (actionName ?? "").trim();
  if (!query) return { ok: false, error: "Action name is required." };

  // `sd.actionDefinitions` is the authoritative list populated at project load.
  // Fetched via the flat context helper (same pattern the rest of the file uses).
  const sdValues = getValues(sdInstance) as { actionDefinitions?: IActionDefinitionDto[] };
  const allDefs: IActionDefinitionDto[] = sdValues.actionDefinitions || [];
  const active = allDefs.filter((a) => {
    const d = a as DefLike;
    return d.isDeprecated !== true && d.isDeleted !== true;
  });

  // Workflow visibility rules — force workflow dashboard regardless of what's shown.
  const ctx = { ...buildActionFilterContextFromSd(sdInstance), dashboardState: DashboardState.workFlow };
  const workflowVisible = active.filter((a) => isActionDefinitionVisible(a, ctx));

  const loweredQuery = query.toLowerCase();

  const visibleExact = workflowVisible.find((a) => nameOf(a) === loweredQuery);
  if (visibleExact) return { ok: true, def: visibleExact };

  const activeExact = active.find((a) => nameOf(a) === loweredQuery);
  if (activeExact) {
    const alts = findClosestActionNames(query, workflowVisible).slice(0, 3);
    const altList = alts.length ? alts.join(", ") : "(none — refine your search_component_metadata query)";
    return {
      ok: false,
      error:
        `Action '${query}' exists but is not allowed in workflow steps ` +
        `(UI-only: e.g. SystemJSFunctions, Navigation, DownloadReport). ` +
        `Workflow-valid alternatives closest to your query: ${altList}. ` +
        `Use search_component_metadata with category='action' to find the right one.`
    };
  }

  const alts = findClosestActionNames(query, workflowVisible).slice(0, 3);
  const altList = alts.length ? alts.join(", ") : "(none)";
  return {
    ok: false,
    error:
      `Action '${query}' not found. Closest workflow-valid matches: ${altList}. ` +
      `Use search_component_metadata({ query: '<what it does>', category: 'action' }) to find the exact name.`
  };
}

/** Rank workflow-visible actions by relevance to the guessed/misspelled name. */
export function findClosestActionNames(query: string, pool: IActionDefinitionDto[]): string[] {
  const q = query.toLowerCase();
  const scored = pool
    .map((a) => {
      const d = a as DefLike;
      const name = d.name || d.Name || "";
      const lower = name.toLowerCase();
      let score = 0;
      if (lower === q) score = 100;
      else if (lower.includes(q)) score = 60;
      else if (q.includes(lower) && lower.length >= 3) score = 40;
      else {
        // Shared prefix length — rough fuzzy.
        let i = 0;
        while (i < q.length && i < lower.length && q[i] === lower[i]) i++;
        score = i >= 3 ? i : 0;
      }
      return { name, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.map((x) => x.name);
}

export interface WorkflowVisibleAction {
  name: string;
  parameters: string[];
  returnType?: string;
}

/**
 * Split an action name into lowercased word tokens: PascalCase / camelCase
 * boundaries, plus digits and separators. "SendMail" → ["send","mail"],
 * "Update3DSecure" → ["update","3","d","secure"]. Used so a query like
 * "send mail" lexically overlaps the name "SendMail".
 */
function tokenizeActionName(name: string): string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .map((t) => t.toLowerCase())
    .filter((t) => t.length > 0);
}

/**
 * INCLUSIVE relevance score (>= 0 for every action — NEVER drops). A higher
 * score floats an action toward the top when a query is given; a 0 score keeps
 * it in the catalog (tail), so the model still SEES it. This is the key
 * difference from `findClosestActionNames`, which FILTERS to score>0 and is
 * therefore wrong for discovery (a synonym query like "notify requester" scores
 * 0 against the name "SendMail" and would hide it — exactly the spike-1 bug).
 */
function scoreActionForQuery(queryTokens: string[], loweredQuery: string, name: string): number {
  const lower: string = name.toLowerCase();
  if (lower === loweredQuery) return 1000;
  let score: number = 0;
  if (loweredQuery.length >= 3 && lower.includes(loweredQuery)) score += 60;
  const nameTokens: string[] = tokenizeActionName(name);
  for (const qt of queryTokens) {
    if (qt.length < 2) continue;
    if (nameTokens.includes(qt)) score += 20;
    else if (nameTokens.some((nt) => nt.startsWith(qt) || qt.startsWith(nt))) score += 8;
  }
  return score;
}

/**
 * Deterministic, workflow-valid action catalog for the WorkflowBuilder's
 * STEP_ACTION discovery. Applies the SAME visibility filter
 * `resolveWorkflowAction` uses (active ∧ `isActionDefinitionVisible` under the
 * workflow dashboard), so the agent is handed only attachable actions — it
 * never picks a UI-only action (Notify / Navigation / DownloadReport) and
 * never has to fall back to the fuzzy `search_component_metadata` vector index
 * that surfaces irrelevant catalog entries (KOA-17362 workflow-action gap).
 *
 * Returns the FULL workflow-visible catalog (up to `limit`) — `query` only
 * REORDERS it (relevance-first), it NEVER drops entries. This is deliberate
 * (spike-1 fix): a lexical query can't bridge synonyms ("notify" ↛ "SendMail"),
 * so filtering by it hid the right action and the builder attached nothing.
 * Showing the whole catalog lets the model map intent→action itself.
 * `total` is the full workflow-visible count; `truncated` is true when the
 * catalog exceeded `limit`.
 */
export function listWorkflowVisibleActions(
  sdInstance: ScreenDesignInstance,
  query?: string,
  limit: number = 250
): { actions: WorkflowVisibleAction[]; total: number; truncated: boolean } {
  const sdValues = getValues(sdInstance) as { actionDefinitions?: IActionDefinitionDto[] };
  const allDefs: IActionDefinitionDto[] = sdValues.actionDefinitions || [];
  const active = allDefs.filter((a) => {
    const d = a as DefLike;
    return d.isDeprecated !== true && d.isDeleted !== true;
  });
  const ctx = { ...buildActionFilterContextFromSd(sdInstance), dashboardState: DashboardState.workFlow };
  const workflowVisible: IActionDefinitionDto[] = active.filter((a) => isActionDefinitionVisible(a, ctx));
  const total: number = workflowVisible.length;

  const nameOfDef = (a: IActionDefinitionDto): string => (a as DefLike).name || (a as DefLike).Name || "";

  const toView = (a: IActionDefinitionDto): WorkflowVisibleAction => {
    const d = a as {
      actionParameters?: { name?: string; Name?: string }[];
      returnTypeName?: string;
      ReturnTypeName?: string;
    };
    const parameters: string[] = (d.actionParameters ?? [])
      .map((p) => p.name || p.Name || "")
      .filter((n): n is string => n.length > 0);
    return { name: nameOfDef(a), parameters, returnType: d.returnTypeName || d.ReturnTypeName };
  };

  // Stable order: alphabetical by default; relevance-first (descending score,
  // alphabetical tie-break) when a query is given. NEVER filter — only reorder.
  const ordered: IActionDefinitionDto[] = [...workflowVisible];
  const trimmed: string = (query ?? "").trim();
  if (trimmed) {
    const loweredQuery: string = trimmed.toLowerCase();
    const queryTokens: string[] = tokenizeActionName(trimmed);
    const scoreOf: Map<IActionDefinitionDto, number> = new Map(
      workflowVisible.map((a) => [a, scoreActionForQuery(queryTokens, loweredQuery, nameOfDef(a))])
    );
    ordered.sort((a, b) => {
      const diff: number = (scoreOf.get(b) ?? 0) - (scoreOf.get(a) ?? 0);
      return diff !== 0 ? diff : nameOfDef(a).localeCompare(nameOfDef(b));
    });
  } else {
    ordered.sort((a, b) => nameOfDef(a).localeCompare(nameOfDef(b)));
  }

  return { actions: ordered.slice(0, limit).map(toView), total, truncated: total > limit };
}

export interface BuiltStepAction {
  action: Record<string, unknown>;
  label: string;
}

/** Build the stored Action object the workflow step expects. */
export function buildStepAction(actionDef: IActionDefinitionDto): BuiltStepAction {
  const def = actionDef as {
    id?: string;
    Id?: string;
    name?: string;
    Name?: string;
    displayName?: string;
    actionParameters?: unknown[];
    returnTypeName?: string;
    ReturnTypeName?: string;
    returnCardinality?: number;
    ReturnCardinality?: number;
  };
  const label = def.displayName || def.name || def.Name || "";
  const parameters = def.actionParameters
    ? (JSON.parse(JSON.stringify(def.actionParameters)) as Array<{ symbol?: unknown }>)
    : [];
  parameters.forEach((p) => {
    if (!p.symbol) {
      p.symbol = { category: 1, fixedValue: "", description: "Fixed Value", label: "Fixed Value", isFixed: true };
    }
  });
  const action: Record<string, unknown> = {
    id: globalThis.crypto ? globalThis.crypto.randomUUID() : Math.random().toString(),
    actionDefinitionId: def.id || def.Id,
    label,
    description: def.name || def.Name,
    parameters,
    returnType: def.returnTypeName || def.ReturnTypeName,
    returnCardinality: def.returnCardinality || def.ReturnCardinality
  };
  return { action, label };
}
